import React, { createContext, useContext, useState, useCallback, useRef, ReactNode } from 'react';

interface SharedScrollContextType {
    sharedScrollLeft: number;
    updateSharedScroll: (newScrollLeft: number) => void;
    registerScrollRef: (ref: HTMLDivElement | null) => void;
    unregisterScrollRef: (ref: HTMLDivElement | null) => void;
    syncAllScrollPositions: () => void;
}

const SharedScrollContext = createContext<SharedScrollContextType | undefined>(undefined);

export const useSharedScroll = (): SharedScrollContextType => {
    const context = useContext(SharedScrollContext);
    if (!context) {
        throw new Error('useSharedScroll must be used within a SharedScrollProvider');
    }
    return context;
};

interface SharedScrollProviderProps {
    children: ReactNode;
}

export const SharedScrollProvider: React.FC<SharedScrollProviderProps> = ({ children }) => {
    const [sharedScrollLeft, setSharedScrollLeft] = useState(0);
    const scrollRefs = useRef<Set<HTMLDivElement>>(new Set());

    const registerScrollRef = useCallback((ref: HTMLDivElement | null) => {
        if (ref && !scrollRefs.current.has(ref)) {
            scrollRefs.current.add(ref);
            // Set the initial scroll position
            ref.scrollLeft = sharedScrollLeft;
        }
    }, [sharedScrollLeft]);

    const unregisterScrollRef = useCallback((ref: HTMLDivElement | null) => {
        if (ref) {
            scrollRefs.current.delete(ref);
        }
    }, []);

    const updateSharedScroll = useCallback((newScrollLeft: number) => {
        setSharedScrollLeft(newScrollLeft);

        // Update all registered scroll containers
        scrollRefs.current.forEach(ref => {
            if (ref && ref.scrollLeft !== newScrollLeft) {
                // Use requestAnimationFrame to ensure smooth scrolling
                requestAnimationFrame(() => {
                    if (ref && ref.scrollLeft !== newScrollLeft) {
                        ref.scrollLeft = newScrollLeft;
                    }
                });
            }
        });
    }, []);

    const syncAllScrollPositions = useCallback(() => {
        // Force sync all scroll positions to the current shared value
        scrollRefs.current.forEach(ref => {
            if (ref && ref.scrollLeft !== sharedScrollLeft) {
                ref.scrollLeft = sharedScrollLeft;
            }
        });
    }, [sharedScrollLeft]);

    const value: SharedScrollContextType = {
        sharedScrollLeft,
        updateSharedScroll,
        registerScrollRef,
        unregisterScrollRef,
        syncAllScrollPositions
    };

    return (
        <SharedScrollContext.Provider value={value}>
            {children}
        </SharedScrollContext.Provider>
    );
};
